import { useMsal } from '@azure/msal-react'
import {
  faChartLine,
  faEnvelope,
  faExpand,
  faInfoCircle,
  faLocationDot,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  ActionIcon,
  AspectRatio,
  Button,
  Group,
  Image,
  Paper,
  Stack,
  Text,
  Title,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useSessionInfo } from '../contexts/SessionContext'
import {
  cancelDeviceAdvertisementAutoRenew,
  updateDeviceAdvertisementStatus,
} from '../data/advertisement.service'
import { DeviceAdvertisementsForHost } from '../models/DeviceAdvertisementsForHost'
import { AdStatuses, COLORS, defaultImageUrl } from '../utils/constants'
import { convertServerDateToLocal } from '../utils/dateUtils'
import { AdStatusModal } from './AdStatusModal'
import { CancelAutoRenewModal } from './CancelAutoRenewModal'

const getDaysLabel = (numberOfDays: number): string => {
  switch (numberOfDays) {
    case 7:
      return '1 week'
    case 14:
      return '2 weeks'
    case 30:
      return '1 month'
    case 90:
      return '3 months'
    default:
      return `${numberOfDays} days`
  }
}

interface AdCardProps {
  ad: DeviceAdvertisementsForHost
  isPending?: boolean
  onApprove?: (adId: number) => void
  onDeny?: (ad: DeviceAdvertisementsForHost) => void
  onExpand: (ad: DeviceAdvertisementsForHost) => void
  approvingAdId?: number | null
  onAdUpdated?: (updatedAd: DeviceAdvertisementsForHost) => void
  isHostingView: boolean
}

export function AdCard({
  ad,
  isPending = false,
  onApprove,
  onDeny,
  onExpand,
  approvingAdId = null,
  onAdUpdated,
  isHostingView,
}: AdCardProps) {
  const { instance } = useMsal()
  const { session } = useSessionInfo()
  const [isCancelling, setIsCancelling] = useState(false)
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [showStatusModal, setShowStatusModal] = useState(false)

  const isOwnAd = session?.organizationId === ad.organizationId
  const showStatusButton =
    (isHostingView && ad.status === AdStatuses.Running) ||
    (ad.status === AdStatuses.Disabled && isOwnAd)
  const buttonText =
    ad.status === AdStatuses.Disabled ? 'Re-Enable Ad' : 'Disable Ad'

  const handleCancelAutoRenew = async () => {
    try {
      setIsCancelling(true)
      const updatedAd = await cancelDeviceAdvertisementAutoRenew(
        ad.id,
        instance,
      )
      if (onAdUpdated) {
        onAdUpdated(updatedAd)
      }
      notifications.show({
        title: 'Success',
        message: 'Auto-renewal has been cancelled',
        color: 'green',
      })
      setShowCancelModal(false)
    } catch (error) {
      console.error('Error cancelling auto-renewal:', error)
      notifications.show({
        title: 'Error',
        message: 'Failed to cancel auto-renewal',
        color: 'red',
      })
    } finally {
      setIsCancelling(false)
    }
  }

  const handleUpdateAdStatus = async () => {
    const isEnabled = ad.status !== AdStatuses.Disabled
    try {
      setIsUpdatingStatus(true)
      const updatedAd = await updateDeviceAdvertisementStatus(
        ad.id,
        instance,
        !isEnabled,
      )

      if (onAdUpdated) {
        onAdUpdated(updatedAd)
      }
      notifications.show({
        title: 'Success',
        message: isEnabled
          ? 'Advertisement has been disabled'
          : 'Advertisement has been enabled',
        color: 'green',
      })
      setShowStatusModal(false)
    } catch (error) {
      console.error('Error updating advertisement status:', error)
      notifications.show({
        title: 'Error',
        message: isEnabled
          ? 'Failed to disable advertisement'
          : 'Failed to enable advertisement',
        color: 'red',
      })
    } finally {
      setIsUpdatingStatus(false)
    }
  }

  return (
    <Paper withBorder p='md' radius='md' key={ad.id}>
      <Group align='flex-start' gap='xl'>
        <div style={{ position: 'relative' }}>
          <AspectRatio ratio={16 / 9} h={175} w={311}>
            <Image
              src={ad.adImageUrl || defaultImageUrl}
              radius='md'
              alt={ad.organizationName}
            />
          </AspectRatio>
          {ad.adImageUrl && (
            <ActionIcon
              variant='filled'
              radius='md'
              onClick={() => onExpand(ad)}
              style={{
                position: 'absolute',
                bottom: 8,
                right: 8,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <FontAwesomeIcon
                icon={faExpand}
                style={{ fontSize: '14px', color: 'white' }}
              />
            </ActionIcon>
          )}
        </div>
        <Stack style={{ flex: 1 }}>
          <Group justify='space-between' align='center' wrap='nowrap'>
            <Group gap='sm' align='center'>
              {isHostingView ? (
                <Title order={2}>{ad.organizationName}</Title>
              ) : (
                <Link
                  to={`/locations/${ad.locationId}`}
                  style={{ color: COLORS.HiddenGemzRed, textDecoration: 'none' }}
                >
                  <Title order={2} c={COLORS.HiddenGemzRed}>{ad.locationName}</Title>
                </Link>
              )}
            </Group>
            {/* Desktop buttons */}
            <Group gap='lg' visibleFrom='sm'>
              {!isHostingView &&
                (ad.status === AdStatuses.Running ||
                  ad.status === AdStatuses.Completed ||
                  ad.status === AdStatuses.Disabled) && (
                  <Text
                    component={Link}
                    to={`/dashboard/myAds/${ad.id}`}
                    c={COLORS.LightGreen}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      textDecoration: 'none',
                      fontWeight: 500,
                    }}
                  >
                    <FontAwesomeIcon icon={faChartLine} />
                    View Metrics
                  </Text>
                )}
              {isPending && onApprove && onDeny ? (
                <>
                  <Text
                    component='button'
                    onClick={() => onDeny(ad)}
                    c={COLORS.HiddenGemzRed}
                    style={{
                      textDecoration: 'underline',
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                    }}
                  >
                    Deny
                  </Text>
                  <Button
                    color={COLORS.LightGreen}
                    onClick={() => onApprove(ad.id)}
                    loading={approvingAdId === ad.id}
                    loaderProps={{ type: 'dots' }}
                  >
                    Approve
                  </Button>
                </>
              ) : (ad.status === AdStatuses.PendingDesign ||
                  ad.status === AdStatuses.PendingAdvertiserApproval ||
                  ad.status === AdStatuses.ChangesRequested) &&
                !isHostingView ? (
                <Text
                  component={Link}
                  to={`/dashboard/designRequests/${ad.advertisementId}`}
                  c={COLORS.HiddenGemzRed}
                  style={{
                    textDecoration: 'underline',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                  }}
                >
                  View Details
                </Text>
              ) : showStatusButton ? (
                <Text
                  component='button'
                  onClick={() => setShowStatusModal(true)}
                  c={COLORS.HiddenGemzRed}
                  style={{
                    textDecoration: 'underline',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                  }}
                >
                  {buttonText}
                </Text>
              ) : (
                !isHostingView &&
                !isPending &&
                ad.status === AdStatuses.Running &&
                ad.autoRenew &&
                !ad.autoRenewCancelled && (
                  <Text
                    component='button'
                    onClick={() => setShowCancelModal(true)}
                    c={COLORS.HiddenGemzRed}
                    style={{
                      textDecoration: 'underline',
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                    }}
                  >
                    Cancel Auto-Renewal
                  </Text>
                )
              )}
            </Group>
          </Group>
          <Group>
            <Text
              component='a'
              href={`mailto:${
                ad.status === AdStatuses.PendingDesign ||
                ad.status === AdStatuses.PendingAdvertiserApproval ||
                ad.status === AdStatuses.PendingContentCheck
                  ? 'support@hiddengemz.tv'
                  : isHostingView
                  ? ad.advertiserEmail
                  : ad.hostEmail
              }`}
              c={COLORS.HiddenGemzRed}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                paddingLeft: '4px',
              }}
            >
              <FontAwesomeIcon
                icon={faEnvelope}
                fixedWidth
                style={{ color: COLORS.HiddenGemzRed, width: '16px' }}
              />
              Email{' '}
              {ad.status === AdStatuses.PendingDesign ||
              ad.status === AdStatuses.PendingAdvertiserApproval ||
              ad.status === AdStatuses.PendingContentCheck
                ? 'Hidden Gemz'
                : isHostingView
                ? 'Advertiser'
                : 'Host'}
            </Text>
          </Group>
          {isHostingView && (
            <Group>
              <Text
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  paddingLeft: '4px',
                }}
              >
                <FontAwesomeIcon
                  icon={faLocationDot}
                  fixedWidth
                  style={{ color: COLORS.HiddenGemzRed, width: '16px' }}
                />
                <Text
                  component={Link}
                  to={`/locations/${ad.locationId}`}
                  style={{
                    color: COLORS.HiddenGemzRed,
                    textDecoration: 'underline',
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    ':hover': {
                      color: COLORS.HiddenGemzRed,
                      textDecoration: 'none',
                    },
                  }}
                  size='sm'
                >
                  {ad.locationName}
                </Text>
              </Text>
            </Group>
          )}
          <Group>
            <Text
              fw={500}
              fz={{ base: 'xs', sm: 'md' }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                paddingLeft: '4px',
              }}
            >
              <FontAwesomeIcon
                icon={faInfoCircle}
                fixedWidth
                style={{ color: '#666', width: '16px' }}
              />
              {ad.deviceName || 'All Devices'} |{' '}
              {ad.autoRenew ? 'Recurring' : 'Single Run'} |{' '}
              {ad.startDateLocal && ad.endDateLocal
                ? `${new Date(
                    ad.startDateLocal,
                  )?.toLocaleDateString()} - ${new Date(
                    ad.endDateLocal,
                  )?.toLocaleDateString()}`
                : ad.numberOfDaysToRunAd
                ? getDaysLabel(ad.numberOfDaysToRunAd)
                : 'Not specified'}
            </Text>
          </Group>
          {ad.status === AdStatuses.Denied && ad.denialReason && (
            <Group>
              <Text
                fw={500}
                fz={{ base: 'xs', sm: 'md' }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  paddingLeft: '4px',
                }}
              >
                <FontAwesomeIcon
                  icon={faInfoCircle}
                  fixedWidth
                  style={{ color: '#666', width: '16px' }}
                />
                Denial Reason: {ad.denialReason}
              </Text>
            </Group>
          )}
          {ad.autoRenewCancelled && ad.autoRenewCancelledDate && (
            <Group>
              <Text
                fw={500}
                fz={{ base: 'xs', sm: 'md' }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  paddingLeft: '4px',
                }}
              >
                <FontAwesomeIcon
                  icon={faInfoCircle}
                  fixedWidth
                  style={{ color: '#666', width: '16px' }}
                />
                Auto-renewal cancelled on{' '}
                {convertServerDateToLocal(
                  ad.autoRenewCancelledDate,
                )?.toLocaleDateString()}
              </Text>
            </Group>
          )}
          {/* Mobile buttons */}
          <Group gap='lg' hiddenFrom='sm' mt='md' justify='flex-end'>
            {!isHostingView &&
              (ad.status === AdStatuses.Running ||
                ad.status === AdStatuses.Completed ||
                ad.status === AdStatuses.Disabled) && (
                <Text
                  component={Link}
                  to={`/dashboard/myAds/${ad.id}`}
                  c={COLORS.LightGreen}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    textDecoration: 'none',
                    fontWeight: 500,
                  }}
                >
                  <FontAwesomeIcon icon={faChartLine} />
                  View Metrics
                </Text>
              )}
            {isPending && onApprove && onDeny ? (
              <>
                <Text
                  component='button'
                  onClick={() => onDeny(ad)}
                  c={COLORS.HiddenGemzRed}
                  style={{
                    textDecoration: 'underline',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                  }}
                >
                  Deny
                </Text>
                <Button
                  color={COLORS.LightGreen}
                  onClick={() => onApprove(ad.id)}
                  loading={approvingAdId === ad.id}
                  loaderProps={{ type: 'dots' }}
                >
                  Approve
                </Button>
              </>
            ) : (ad.status === AdStatuses.PendingDesign ||
                ad.status === AdStatuses.PendingAdvertiserApproval ||
                ad.status === AdStatuses.ChangesRequested) &&
              !isHostingView ? (
              <Text
                component={Link}
                to={`/dashboard/designRequests/${ad.advertisementId}`}
                c={COLORS.HiddenGemzRed}
                style={{
                  textDecoration: 'underline',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                }}
              >
                View Details
              </Text>
            ) : showStatusButton ? (
              <Text
                component='button'
                onClick={() => setShowStatusModal(true)}
                c={COLORS.HiddenGemzRed}
                style={{
                  textDecoration: 'underline',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                }}
              >
                {buttonText}
              </Text>
            ) : (
              !isHostingView &&
              !isPending &&
              ad.status === AdStatuses.Running &&
              ad.autoRenew &&
              !ad.autoRenewCancelled && (
                <Text
                  component='button'
                  onClick={() => setShowCancelModal(true)}
                  c={COLORS.HiddenGemzRed}
                  style={{
                    textDecoration: 'underline',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                  }}
                >
                  Cancel Auto-Renewal
                </Text>
              )
            )}
          </Group>
        </Stack>
      </Group>
      <CancelAutoRenewModal
        ad={ad}
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={handleCancelAutoRenew}
        isCancelling={isCancelling}
      />
      <AdStatusModal
        ad={ad}
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        onConfirm={handleUpdateAdStatus}
        isUpdating={isUpdatingStatus}
      />
    </Paper>
  )
}
